"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, MessageSquareQuote } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Will I work directly with you or an account manager?",
  answer: "Yes, you work directly with me. I personally handle your local strategy, technical audits, and implementation guidance without account managers or middlemen."
}, {
  id: "item-2",
  question: "How long does it typically take to see measurable SEO results?",
  answer: "Initial technical quick wins and keyword optimization usually reflect in search console rankings within 4 to 8 weeks. Sustainable, high-converting organic growth for competitive terms typically develops over 3 to 6 months of focused authority building."
}, {
  id: "item-3",
  question: "How do Google Maps and local SEO rankings actually work?",
  answer: "Local visibility is driven by a combination of verified Google Business Profile optimization, localized citation accuracy, geographically targeted site copy, and authentic review signals. I optimize every layer to capture the high-intent local map pack."
}, {
  id: "item-4",
  question: "Do I need separate landing pages if I service multiple locations?",
  answer: "Yes, if you serve distinct geographic areas, dedicated location pages tailored to local needs perform significantly better. I build high-value, unique landing pages for each target location rather than duplicating generic copy."
}, {
  id: "item-5",
  question: "What is AI Search Optimisation and how does it fit into SEO?",
  answer: "AI Search Optimisation prepares your website for AI-driven platforms like Google SGE, Perplexity, and ChatGPT. By embedding structured schema, factual clarity, and authoritative depth, I ensure your brand gets cited across modern conversational search engines."
}, {
  id: "item-6",
  question: "What deliverables and reporting can I expect each month?",
  answer: "You receive clear, transparent reporting focused on organic revenue, lead volume, and keyword rankings rather than vanity metrics. Every deliverable comes with practical technical recommendations and direct access to discuss next steps."
}, {
  id: "item-7",
  question: "How does Google Business Profile SEO support local rankings?",
  answer: "Google Business Profile SEO is fundamental to capturing local search demand and map pack placements. I configure and optimize your profile categories, service listings, attributes, and geotagged updates so Google recognizes your authority across your exact service areas."
}, {
  id: "item-8",
  question: "What does working with an independent local search consultant look like?",
  answer: "Engagements follow structured monthly sprints covering citation cleanup, technical fixes, Google Business Profile enhancements, and localized landing pages. You receive clear sprint roadmaps, direct communication, and monthly performance reviews tracking local visibility and inbound inquiries."
}];
export default function FaqAccordion() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="faqaccordion" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full">
                <HelpCircle className="size-3.5 text-primary" />
                <span>{"Clear Answers"}</span>
              </Badge>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-foreground tracking-tight leading-tight">
              {"Frequently Asked Questions"}
            </h2>

            <p className="text-muted-foreground text-base leading-relaxed">
              {"Get practical, honest answers about search strategy, timelines, and working directly with an independent SEO consultant."}
            </p>

            <div className="p-6 rounded-2xl bg-card border border-border shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <MessageSquareQuote className="size-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-card-foreground">
                    {"Have a specific question?"}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {"No sales team. Speak directly with Yassine."}
                  </p>
                </div>
              </div>

              <div className="pt-2 space-y-2.5">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium rounded-lg shadow-sm">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    <span>{"Book a Free SEO Consultation"}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full border-border bg-transparent text-foreground hover:bg-muted font-medium rounded-lg">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    <span>{"Get a Free SEO Audit"}</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-4 w-full">
              {faqList.map((faq, i) => <div data-index={i} key={faq.id}>
                  <motion.div variants={itemVariants}>
                    <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-6 py-2 shadow-2xs transition-all duration-200 hover:border-primary/30">
                      <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-card-foreground hover:no-underline hover:text-primary transition-colors py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                </div>)}
            </Accordion>
          </motion.div>

        </div>
      </div>
    </section>;
}